import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import { EmptyState, IconButton, Page, PageHeader, Segmented, TableSkeleton } from "../../../components/ui";
import { getRevenue } from "../../../services/revenue";
import { formatMoney, TYPE_COLORS, TYPES } from "../payments-ledger/shared";
import "../payments-ledger/payments.css";
import "./revenue.css";

const muted = { colors: "#9b9ba6" };
const fmt = (n) => new Intl.NumberFormat().format(n || 0);
const monthLabel = (key, long = false) =>
  new Date(`${key}-01T00:00:00Z`).toLocaleDateString([], { month: long ? "long" : "short", year: "2-digit", timeZone: "UTC" });

const baseOptions = (id, months) => ({
  chart: { id, background: "transparent", toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit", animations: { enabled: false } },
  dataLabels: { enabled: false },
  grid: { borderColor: "rgba(255, 255, 255, 0.06)", strokeDashArray: 4 },
  legend: { position: "top", horizontalAlign: "right", labels: muted },
  labels: months.map((m) => m.month),
  xaxis: {
    type: "category",
    labels: { style: muted, rotate: 0, hideOverlappingLabels: true, formatter: (value) => (value ? monthLabel(value) : "") },
    axisBorder: { show: false },
    axisTicks: { show: false },
    tooltip: { enabled: false },
  },
  plotOptions: { bar: { columnWidth: months.length > 12 ? "75%" : "55%", borderRadius: 2 } },
});

/** Revenue per month in one currency, stacked by payment type. */
export const revenueChart = (data, t) => {
  const types = Object.keys(TYPE_COLORS).filter((type) => data.monthly.some((m) => m.types[type]?.amount));
  const money = (n) => formatMoney(n, data.currency);
  const base = baseOptions("revenue-months", data.monthly);
  return {
    series: types.map((type) => ({ name: t(TYPES[type]?.label || "Other"), data: data.monthly.map((m) => m.types[type]?.amount || 0) })),
    options: {
      ...base,
      chart: { ...base.chart, stacked: true },
      colors: types.map((type) => TYPE_COLORS[type]),
      yaxis: { min: 0, forceNiceScale: true, labels: { style: muted, formatter: money } },
      tooltip: { theme: "dark", shared: true, intersect: false, x: { formatter: (v, { dataPointIndex }) => monthLabel(data.monthly[dataPointIndex]?.month || "", true) }, y: { formatter: money } },
    },
  };
};

/** New subscribers and departures per month (columns), churn rate (line, right axis). */
export const subscribersChart = (data, t) => {
  const base = baseOptions("revenue-subscribers", data.monthly);
  return {
    series: [
      { name: t("New subscribers"), type: "column", data: data.monthly.map((m) => m.newSubscribers) },
      { name: t("Departures"), type: "column", data: data.monthly.map((m) => m.churned) },
      { name: t("Churn rate"), type: "line", data: data.monthly.map((m) => m.churnRate ?? 0) },
    ],
    options: {
      ...base,
      colors: ["#1176BC", "#ef5d8f", "#CDAE6E"],
      stroke: { width: [0, 0, 3], curve: "smooth" },
      markers: { size: data.monthly.length <= 12 ? 4 : 0, strokeWidth: 0 },
      yaxis: [
        { seriesName: t("New subscribers"), min: 0, forceNiceScale: true, labels: { style: muted, formatter: (v) => fmt(Math.round(v)) } },
        { seriesName: t("New subscribers"), show: false },
        { seriesName: t("Churn rate"), opposite: true, min: 0, forceNiceScale: true, labels: { style: muted, formatter: (v) => `${Math.round(v)} %` } },
      ],
      tooltip: {
        theme: "dark",
        shared: true,
        intersect: false,
        x: { formatter: (v, { dataPointIndex }) => monthLabel(data.monthly[dataPointIndex]?.month || "", true) },
        y: { formatter: (v, { seriesIndex }) => (seriesIndex === 2 ? `${String(v).replace(".", ",")} %` : fmt(v)) },
      },
    },
  };
};

const Kpi = ({ value, label, hint, tone, to }) => {
  const body = (
    <>
      <strong>{value ?? "—"}</strong>
      <span>{label}</span>
      {hint && <small className="revenue-kpi-hint">{hint}</small>}
    </>
  );
  return to ? (
    <Link to={to} className="ui-card ledger-stat" data-tone={tone}>
      {body}
    </Link>
  ) : (
    <div className="ui-card ledger-stat" data-tone={tone}>
      {body}
    </div>
  );
};

/**
 * Revenue dashboard: paying subscribers, monthly recurring revenue, revenue per
 * month and per country, new subscribers and departures, upcoming renewals,
 * cancellation reasons.
 */
const Revenue = () => {
  const { t } = useTranslation();
  const [months, setMonths] = useState(12);
  const [currency, setCurrency] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(false);
    getRevenue({ months, currency })
      .then((result) => alive && setData(result))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [months, currency, attempt]);

  const money = (n) => formatMoney(n, data?.currency || "USD");
  const mainMrr = data?.mrr.find((row) => row.currency === data.currency);
  const otherMrr = data?.mrr.filter((row) => row.currency !== data.currency) || [];
  const s = data?.subscribers;
  const revenue = data ? revenueChart(data, t) : null;
  const subscribers = data ? subscribersChart(data, t) : null;
  const total = data ? data.monthly.reduce((sum, m) => sum + m.revenue, 0) : 0;

  return (
    <Page>
      <PageHeader
        title={t("Revenue")}
        subtitle={t("Subscribers, recurring revenue, departures and revenue per country.")}
        actions={<IconButton icon="ri-refresh-line" label={t("Refresh")} onClick={() => setAttempt((n) => n + 1)} />}
      />

      <div className="revenue-toolbar">
        <Segmented
          label={t("Period")}
          value={months}
          onChange={setMonths}
          options={[
            { value: 3, label: t("3 months") },
            { value: 6, label: t("6 months") },
            { value: 12, label: t("12 months") },
            { value: 24, label: t("24 months") },
          ]}
        />
        {data?.currencies?.length > 1 && (
          <select className="ui-input revenue-currency" aria-label={t("Currency")} value={data.currency} onChange={(e) => setCurrency(e.target.value)}>
            {data.currencies.map((row) => (
              <option key={row.currency} value={row.currency}>
                {row.currency}
              </option>
            ))}
          </select>
        )}
      </div>

      {error ? (
        <section className="ui-card">
          <EmptyState icon="ri-error-warning-line">
            {t("The revenue could not be loaded.")}{" "}
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setAttempt((n) => n + 1)}>
              {t("Retry")}
            </button>
          </EmptyState>
        </section>
      ) : !data ? (
        <section className="ui-card">
          <TableSkeleton rows={6} />
        </section>
      ) : (
        <>
          <div className="ledger-stats revenue-kpis">
            <Kpi
              value={mainMrr ? money(mainMrr.mrr) : money(0)}
              label={t("Monthly recurring revenue (MRR)")}
              hint={[
                mainMrr ? t("{{amount}} per year", { amount: money(mainMrr.arr) }) : null,
                ...otherMrr.map((row) => `+ ${formatMoney(row.mrr, row.currency)}`),
              ].filter(Boolean).join(" · ")}
            />
            <Kpi
              value={fmt(s.total)}
              label={t("Paying subscribers")}
              hint={t("{{monthly}} monthly · {{annual}} annual · {{mobile}} Mobile Money", s)}
            />
            <Kpi value={fmt(s.gifted)} label={t("Offered subscriptions")} />
            <Kpi
              value={data.averageChurnRate === null ? "—" : `${String(data.averageChurnRate).replace(".", ",")} %`}
              label={t("Average churn per month")}
            />
            <Kpi value={fmt(data.upcoming.renewals)} label={t("Renewals in the next 30 days")} hint={data.upcoming.mobileToRenew ? t("{{count}} by Mobile Money (manual)", { count: data.upcoming.mobileToRenew }) : null} />
            <Kpi value={fmt(s.cancelling)} label={t("Cancellations scheduled")} tone={s.cancelling ? "amber" : undefined} />
            <Kpi value={fmt(s.pastDue)} label={t("Unpaid (card refused)")} tone={s.pastDue ? "amber" : undefined} to={s.pastDue ? "/dashboard/unpaid-reminders" : undefined} />
          </div>

          <section className="ui-card revenue-card">
            <div className="revenue-card-head">
              <h2>{t("Revenue per month")}</h2>
              <span className="ui-hint">
                {t("{{amount}} over the period", { amount: money(total) })}
                {data.currencies.length > 1 ? ` · ${t("in {{currency}} only", { currency: data.currency })}` : ""}
              </span>
            </div>
            {revenue.series.length ? (
              <Chart type="bar" height={300} options={revenue.options} series={revenue.series} />
            ) : (
              <EmptyState icon="ri-bar-chart-line">{t("No payment over this period")}</EmptyState>
            )}
          </section>

          <section className="ui-card revenue-card">
            <div className="revenue-card-head">
              <h2>{t("New subscribers and departures")}</h2>
              <span className="ui-hint">{t("Departures: subscriptions ended and not renewed. Offered subscriptions are not counted.")}</span>
            </div>
            <Chart type="line" height={280} options={subscribers.options} series={subscribers.series} />
          </section>

          <div className="revenue-columns">
            <section className="ui-card revenue-card">
              <div className="revenue-card-head">
                <h2>{t("Revenue per country")}</h2>
              </div>
              {data.byCountry.length ? (
                <div className="ui-table-wrap">
                  <table className="ui-table">
                    <thead>
                      <tr>
                        <th>{t("Country")}</th>
                        <th className="revenue-num">{t("Payers")}</th>
                        <th className="revenue-num">{t("Payments")}</th>
                        <th className="revenue-num">{t("Amount")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byCountry.slice(0, 15).map((row) => (
                        <tr key={row.country || "-"}>
                          <td>{row.country || <span className="ui-cell-muted">{t("Unknown (no account)")}</span>}</td>
                          <td className="revenue-num">{fmt(row.payers)}</td>
                          <td className="revenue-num">{fmt(row.count)}</td>
                          <td className="revenue-num">{money(row.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon="ri-earth-line">{t("No payment over this period")}</EmptyState>
              )}
            </section>

            <section className="ui-card revenue-card">
              <div className="revenue-card-head">
                <h2>{t("Why they cancel")}</h2>
              </div>
              {data.cancelReasons.length ? (
                <ul className="revenue-reasons">
                  {data.cancelReasons.map((row) => (
                    <li key={row.reason}>
                      <span>{row.reason}</span>
                      <strong>{fmt(row.count)}</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="ui-hint">{t("No reason given over this period.")}</p>
              )}
            </section>
          </div>

          <p className="ui-hint revenue-note">
            {t("The MRR uses the last amount paid by each paying subscriber (annual divided by 12). Subscribers at the start of a month and departures are estimated from the subscription dates.")}
            {data.mrrUnknown ? ` ${t("{{count}} subscribers without a known payment are not in the MRR.", { count: data.mrrUnknown })}` : ""}
          </p>
        </>
      )}
    </Page>
  );
};

export default Revenue;
